import {
  faBox,
  faCartShopping,
  faHeart,
} from '@fortawesome/pro-regular-svg-icons';
import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Link, NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { WxBankroll, WxIcon, WxLabel } from '../..';
import useBankroll from '../../../hooks/api/queries/useBankroll';
import useFavorites from '../../../hooks/api/queries/useFavorites';
import useMenu from '../../../hooks/api/queries/useMenu';
import { useCartContext } from '../../../hooks/context/useCartContext';
import { useMenuContext } from '../../../hooks/context/useMenuContext';
import useJwt from '../../../hooks/utils/useJwt';
import { __ } from '../../../i18n/l10n';
import { breakpoint } from '../../../styles/constants';
import {
  SLUG_CART,
  SLUG_CONFIRMATION,
  SLUG_DELIVERY,
  SLUG_FAVORITES,
  SLUG_ORDER,
  SLUG_ORDERS,
  SLUG_RECAP,
  SLUG_CATEGORY
} from '../../../styles/slugs';
import { appendCommitHash } from '../../../utils/appendHash';
import { DesktopNavBar } from './DesktopNavbar';
import { isInCategory } from '../../../utils/constructSlug';

export const Nav: React.FC = () => {
  const { open, setOpen, selected, setSelected } = useMenuContext();
  const jwt = useJwt();
  const { cart } = useCartContext();
  const { data: bankroll } = useBankroll();
  let orderScreen = false;
  const location = useLocation();
  const { data: favorites } = useFavorites();
  const { data: menu } = useMenu();
  let navigate = useNavigate();

  switch (location.pathname) {
    case `/${SLUG_CART}`:
      return null;
    case `/${SLUG_DELIVERY}`:
      return null;
    case `/${SLUG_RECAP}`:
      return null;
    case `/${SLUG_CONFIRMATION}`:
      return null;
  }
  if (
    location.pathname.includes(`/${SLUG_ORDERS}`) ||
    location.pathname.includes(`/${SLUG_ORDER}`)
  )
    orderScreen = true;

  return (
    <NavGrid id="navBar">
      <VarContainer>
        <Link to={appendCommitHash('/')}>
          <WxLabel label={__('Shop')} active hover />
        </Link>
      </VarContainer>
      <VarContainer $justify={'end'} $startCol={3}>
        {jwt && (
          <IconsContainer>
            <NavLink to={appendCommitHash(`/${SLUG_ORDERS}`)}>
              {({ isActive }) => (
                <WxIcon
                  icon={faBox}
                  light
                  active={
                    isActive || location.pathname.includes(`${SLUG_ORDER}/`)
                  }
                />
              )}
            </NavLink>
            <NavLink to={appendCommitHash(SLUG_FAVORITES)}>
              {({ isActive }) => (
                <CartRelativeDiv>
                  {(favorites?.length ?? 0) > 0 && (
                    <div>
                      <div>{favorites?.length}</div>
                    </div>
                  )}

                  <WxIcon icon={faHeart} light active={isActive} />
                </CartRelativeDiv>
              )}
            </NavLink>
            <NavLink to={appendCommitHash(`/${SLUG_CART}`)}>
              {({ isActive }) => (
                <CartRelativeDiv>
                  {cart.totalArticles > 0 && (
                    <div>
                      <div>{cart.totalArticles}</div>
                    </div>
                  )}
                  <WxIcon icon={faCartShopping} light active={isActive} />
                </CartRelativeDiv>
              )}
            </NavLink>
          </IconsContainer>
        )}
      </VarContainer>
      <VarContainer $startCol={2} $orderScreen={orderScreen}>
        <MobileOnly>
          {menu?.slice(0, 3).map((item, index) => (
            // Logic duplicated from src/components/layout/nav/DesktopNavbar.tsx
            <div
            style={{ marginBottom: '0.625rem' }}
            key={index}
            onClick={
              item.children === undefined
                ? () =>
                    navigate(
                      appendCommitHash(`/${SLUG_CATEGORY}/${item.slug}`),
                    )
                : selected === index
                ? () => setSelected(-1)
                : () => setSelected(index)
            }
            >
              <WxLabel
                id={selected === index ? 'openSub' : ''}
                label={item.name}
                hover
                active={
                  (open === false && selected === index) ||
                  (selected === -1 &&
                    isInCategory(item, window.location.pathname))
                }
              />
            </div>
          ))}

          <WxLabel label={__('All categories')} onClick={() => setOpen(true)} />
        </MobileOnly>
        <DesktopNavBar />
      </VarContainer>
      <VarContainer $justify={'end'} $startCol={4} $orderScreen={orderScreen} $padding={true}>
        {jwt && bankroll && <WxBankroll balance={bankroll} />}
      </VarContainer>
    </NavGrid>
  );
};

const CartRelativeDiv = styled.div`
  position: relative;
  > div {
    position: absolute;
    text-align: center;
    width: 1rem;
    height: 1rem;
    aspect-ratio: 1;
    line-height: 1rem;
    border-radius: 50%;
    top: -5px;
    right: -5px;
    color: ${(props) => props.theme.colors.white};
    background-color: ${({ theme }) => theme.colors.red};
    font-weight: 900;
    font-size: 0.6rem;
    z-index: 1;

    > div {
      position: relative;
      top: 2%;
      left: -2.5%;
    }
  }
`;

export const MobileOnly = styled.div`
  display: block;
  @media (min-width: ${breakpoint}) {
    display: none;
  }
`;

const NavGrid = styled.nav`
  display: grid;

  @media (min-width: ${breakpoint}) {
    position: sticky;
    box-sizing: border-box;
    top: 0;
    top: -1px;
    width: var(--viewportWidth);
    padding-inline: calc((var(--viewportWidth) - 970px) / 2);
    margin-inline: 50%;
    transform: translateX(-50%);
    background-color: ${({ theme }) => theme.colors.black};
    z-index: 3;
  }

  p {
    font-size: 0.75rem;
  }

  :nth-child(1) {

    @media (min-width: ${breakpoint}) {
      p {
        font-size: 0.75rem;
      }
    }
  }

  grid-template-areas:
    'one two'
    'three four';

  @media (min-width: ${breakpoint}) {
    padding-top: 0.625rem;
    padding-bottom: 0.6rem;
    grid-template-columns: auto 1fr auto auto;
  }
`;

const VarContainer = styled.div<{
  $justify?: string;
  $startCol?: number;
  $orderScreen?: boolean;
  $padding?: boolean
}>`
  display: ${({ $orderScreen }) => ($orderScreen ? 'none' : 'flex')};
  align-items: center;
  justify-content: space-between;
  padding-block: 0.625rem;
  padding-inline: 0;

  justify-self: ${({ $justify }) => ($justify === 'end' ? 'end' : 'start')};

  @media (min-width: ${breakpoint}) {
    display: flex;
    grid-column-start: ${({ $startCol }) => ($startCol ? $startCol : 1)};
    grid-row-start: 1;
    gap: 0.625rem;
    padding-block: 0;

    ${({ $padding }) => $padding ? ':last-child { padding-left: 0.625rem; } :first-child { padding-right: 0.625rem; }' : ''}
  }
`;

const IconsContainer = styled.div`
  display: flex;
  gap: 0.625rem;

  svg {
    width: 16px;
    height: 16px;
    aspect-ratio: 1;
  }
`;
